/**
 * Memorization session state hook.
 *
 * v1: localStorage-backed only. No Supabase writes, no auth dependency.
 * The hook's return shape is the consumer-facing API; the storage
 * backend is internal. When PowerDash / cloud sync lands, we swap the
 * storage layer without touching consumers.
 *
 * One active session per browser. Starting a new session overwrites
 * the previous one in localStorage. `endSession` clears the slot
 * entirely.
 */

import { useCallback, useEffect, useState } from 'react';
import type {
  MemorizationMode,
  MemorizationSessionState,
  RepeatMode,
} from '@/types/memorization';

const STORAGE_KEY = 'alhudah:memorize:v1';

function loadFromStorage(): MemorizationSessionState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MemorizationSessionState;
    // Basic shape validation — discard on any mismatch rather than crash.
    if (
      typeof parsed.surahId !== 'number' ||
      typeof parsed.startAyah !== 'number' ||
      typeof parsed.endAyah !== 'number' ||
      typeof parsed.currentAyah !== 'number' ||
      !Array.isArray(parsed.completedAyahs)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveToStorage(state: MemorizationSessionState | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (state === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  } catch {
    // Quota exceeded or storage disabled — silent fail. Session
    // continues in memory; just won't survive reload.
  }
}

interface StartSessionArgs {
  surahId: number;
  startAyah: number;
  endAyah: number;
}

export interface UseMemorizationSessionReturn {
  /** The persisted session state, or `null` when no session is active. */
  session: MemorizationSessionState | null;
  /** Whether the current ayah index is past the end of the range. */
  isComplete: boolean;
  /** Begin a new session with the given range. Overwrites any prior session. */
  startSession: (args: StartSessionArgs) => void;
  /** Clear the session entirely. Returns the user to setup. */
  endSession: () => void;
  /** Advance to the next ayah within the range. No-op at the end. */
  nextAyah: () => void;
  /** Go back to the previous ayah within the range. No-op at the start. */
  prevAyah: () => void;
  /** Jump directly to a specific ayah (clamped to the range). */
  goToAyah: (ayahNumber: number) => void;
  /** Add an ayah to the completed set. */
  markMemorized: (ayahNumber: number) => void;
  /** Remove an ayah from the completed set (toggle support). */
  unmarkMemorized: (ayahNumber: number) => void;
  /** Switch display mode. */
  setMode: (mode: MemorizationMode) => void;
  /** Switch repeat preference. */
  setRepeatMode: (mode: RepeatMode) => void;
}

export function useMemorizationSession(): UseMemorizationSessionReturn {
  const [session, setSession] = useState<MemorizationSessionState | null>(
    () => loadFromStorage(),
  );

  // Persist on every change.
  useEffect(() => {
    saveToStorage(session);
  }, [session]);

  const startSession = useCallback(
    ({ surahId, startAyah, endAyah }: StartSessionArgs) => {
      const next: MemorizationSessionState = {
        surahId,
        startAyah,
        endAyah,
        currentAyah: startAyah,
        completedAyahs: [],
        mode: 'read',
        repeatMode: 'once',
        startedAt: new Date().toISOString(),
      };
      setSession(next);
    },
    [],
  );

  const endSession = useCallback(() => {
    setSession(null);
  }, []);

  const nextAyah = useCallback(() => {
    setSession((prev) => {
      if (!prev) return prev;
      // Allow the cursor to step ONE past endAyah so the UI can show a
      // "Session Complete" view. Don't go further.
      if (prev.currentAyah > prev.endAyah) return prev;
      return { ...prev, currentAyah: prev.currentAyah + 1 };
    });
  }, []);

  const prevAyah = useCallback(() => {
    setSession((prev) => {
      if (!prev) return prev;
      if (prev.currentAyah <= prev.startAyah) return prev;
      return { ...prev, currentAyah: prev.currentAyah - 1 };
    });
  }, []);

  const goToAyah = useCallback((ayahNumber: number) => {
    setSession((prev) => {
      if (!prev) return prev;
      const clamped = Math.min(
        Math.max(ayahNumber, prev.startAyah),
        prev.endAyah,
      );
      return { ...prev, currentAyah: clamped };
    });
  }, []);

  const markMemorized = useCallback((ayahNumber: number) => {
    setSession((prev) => {
      if (!prev) return prev;
      if (prev.completedAyahs.includes(ayahNumber)) return prev;
      return {
        ...prev,
        completedAyahs: [...prev.completedAyahs, ayahNumber].sort((a, b) => a - b),
      };
    });
  }, []);

  const unmarkMemorized = useCallback((ayahNumber: number) => {
    setSession((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        completedAyahs: prev.completedAyahs.filter((n) => n !== ayahNumber),
      };
    });
  }, []);

  const setMode = useCallback((mode: MemorizationMode) => {
    setSession((prev) => (prev ? { ...prev, mode } : prev));
  }, []);

  const setRepeatMode = useCallback((repeatMode: RepeatMode) => {
    setSession((prev) => (prev ? { ...prev, repeatMode } : prev));
  }, []);

  const isComplete = session ? session.currentAyah > session.endAyah : false;

  return {
    session,
    isComplete,
    startSession,
    endSession,
    nextAyah,
    prevAyah,
    goToAyah,
    markMemorized,
    unmarkMemorized,
    setMode,
    setRepeatMode,
  };
}
